import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import {
  Loader2, CheckCircle2, XCircle, Clock,
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });
};

export default function SeatCancellationPanel({ onChange }) {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [decideTarget, setDecideTarget] = useState(null);   // {req, action: 'approve'|'reject'}
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/seat/cancellation-requests${statusFilter !== 'all' ? '?status=' + statusFilter : ''}`);
      setRows(res.data?.requests || []);
      setCounts(res.data?.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const submitDecision = async () => {
    if (!decideTarget) return;
    setBusy(decideTarget.req.id);
    try {
      const url = `/seat/cancellation-requests/${decideTarget.req.id}/${decideTarget.action}`;
      await api.post(url, { note: note.trim() });
      toast.success(`Request ${decideTarget.action}d`);
      setDecideTarget(null);
      setNote('');
      load();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border bg-white">
          {[['pending', 'Pending', counts.pending], ['approved', 'Approved', counts.approved],
            ['rejected', 'Rejected', counts.rejected], ['all', 'All', counts.pending + counts.approved + counts.rejected]]
            .map(([v, l, n]) => (
              <button key={v} onClick={() => setStatusFilter(v)}
                className={`px-3 py-1.5 text-xs font-medium ${statusFilter === v ? 'bg-emerald-600 text-white' : 'text-slate-700'}`}
                data-testid={`cr-filter-${v}`}>
                {l} <Badge variant="outline" className="ml-1 text-[9px] bg-white">{n}</Badge>
              </button>
            ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">No requests match.</CardContent></Card>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Requested</th>
                  <th className="px-3 py-2 text-left">Member</th>
                  <th className="px-3 py-2 text-left">ITS / HOF</th>
                  <th className="px-3 py-2 text-left">Seat / Floor</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`cr-row-${r.id}`}>
                    <td className="px-3 py-2 text-xs">{(r.created_at || '').replace('T', ' ').split('.')[0]}</td>
                    <td className="px-3 py-2 font-medium">{r.member_name}</td>
                    <td className="px-3 py-2 text-xs">
                      <div>ITS {r.its_id || '—'}</div>
                      <div className="text-slate-500 truncate max-w-[180px]">{r.hof_name}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div>Seat #{r.seat_no}</div>
                      <div className="text-slate-500">{r.floor_name || ''}</div>
                    </td>
                    <td className="px-3 py-2 text-xs max-w-[240px] italic text-slate-600" title={r.reason}>"{r.reason || '—'}"</td>
                    <td className="px-3 py-2 text-center">
                      <Badge className={
                        r.status === 'approved' ? 'bg-emerald-600' :
                        r.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'
                      }>{r.status.toUpperCase()}</Badge>
                      {r.admin_note && <div className="text-[10px] text-slate-500 mt-1 italic">"{r.admin_note}"</div>}
                    </td>
                    <td className="px-3 py-2 text-center space-x-1">
                      {r.status === 'pending' ? (
                        <>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7"
                            onClick={() => { setDecideTarget({ req: r, action: 'approve' }); setNote(''); }}
                            disabled={busy === r.id}
                            data-testid={`cr-approve-${r.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 h-7"
                            onClick={() => { setDecideTarget({ req: r, action: 'reject' }); setNote(''); }}
                            disabled={busy === r.id}
                            data-testid={`cr-reject-${r.id}`}>
                            <XCircle className="w-3 h-3 mr-1" /> Reject
                          </Button>
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">decided {r.decided_at ? new Date(r.decided_at).toLocaleString() : ''}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!decideTarget} onOpenChange={(o) => !o && setDecideTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="cr-decide-dialog">
          <DialogHeader>
            <DialogTitle>{decideTarget?.action === 'approve' ? 'Approve' : 'Reject'} Cancellation</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-slate-50 rounded p-2">
              <div className="font-semibold">{decideTarget?.req?.member_name} · Seat #{decideTarget?.req?.seat_no}</div>
              <div className="text-xs text-slate-500">Reason: "{decideTarget?.req?.reason || '—'}"</div>
            </div>
            {decideTarget?.action === 'approve' && (
              <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                <Clock className="w-3 h-3 inline mr-1" />
                Approving will <strong>immediately free the seat</strong> and remove the booking.
              </p>
            )}
            <div>
              <Label className="text-xs">Optional admin note</Label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)}
                rows={2} className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                placeholder="Will be shown to the HOF." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideTarget(null)}>Cancel</Button>
            <Button
              className={decideTarget?.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-500 hover:bg-red-600'}
              onClick={submitDecision}
              data-testid="cr-decide-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (decideTarget?.action === 'approve' ? 'Approve & Free Seat' : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
